import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import BarcodeScanner from "@/components/BarcodeScanner";
import { PurchaseProductPicker } from "@/components/PurchaseProductPicker";
import { lookupBarcode, UnknownBarcodeDialog } from "@/components/barcode/BarcodeFlow";
import { SerialCount } from "@/components/barcode/SerialCount";
import { formatVariantLabel } from "@/lib/variants";
import { ScanLine, Search, PackagePlus, PackageMinus, ArrowLeftRight, ClipboardCheck, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());
const MODES = [
  ["in", "Stok Girişi", PackagePlus, "bg-emerald-600"], ["out", "Stok Çıkışı", PackageMinus, "bg-rose-600"],
  ["transfer", "Depo Transferi", ArrowLeftRight, "bg-blue-600"], ["count", "Sayım", ClipboardCheck, "bg-amber-600"],
];
const SEL = "w-full h-12 rounded-md border border-slate-700 bg-slate-900 px-3 text-base text-slate-100";

function ManualFind({ onPick }) {
  const [line, setLine] = useState({ material_id: "", product_name: "", variant_id: "", has_variants: false });
  const ready = line.material_id && (!line.has_variants || line.variant_id);
  return (
    <div className="space-y-3" data-testid="ht-manual-find">
      <PurchaseProductPicker index="ht" line={line} onChange={(p) => setLine((l) => ({ ...l, ...p }))} />
      <Button disabled={!ready} onClick={() => onPick(line)} className="w-full h-12 rounded-md bg-blue-600 hover:bg-blue-500 text-base" data-testid="ht-manual-pick">Seç</Button>
    </div>
  );
}

function StockAction({ item, mode, warehouses, rows, onDone }) {
  const [wh, setWh] = useState(warehouses.length === 1 ? warehouses[0].id : "");
  const [tgt, setTgt] = useState("");
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const reqId = useMemo(uid, [mode]);
  const q = Number(String(qty).replace(",", "."));
  const row = rows.find((r) => r.warehouse_id === wh);
  const ok = wh && Number.isFinite(q) && (mode === "count" ? q >= 0 && qty !== "" : q > 0) && (mode !== "transfer" || (tgt && tgt !== wh));
  const base = { product_id: item.product.id, variant_id: item.variant?.id || "" };
  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "in" || mode === "out") {
        await api.post("/inventory/stock/movement", { ...base, warehouse_id: wh, quantity_delta: mode === "in" ? q : -q, kind: "manuel",
          reference_type: "hand_terminal", reason: `El Terminali — ${mode === "in" ? "Stok Girişi" : "Stok Çıkışı"}`, client_request_id: reqId });
      } else if (mode === "transfer") {
        await api.post("/warehouses/transfers", { transfer_id: reqId, source_warehouse_id: wh, target_warehouse_id: tgt, note: "El Terminali", items: [{ ...base, quantity: q }] });
      } else {
        await api.post(`/warehouses/${wh}/stock-count/apply`, { count_id: reqId, note: "El Terminali sayım", items: [{ ...base, counted_qty: q }] });
      }
      toast.success("İşlem kaydedildi");
      setQty("");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid={`ht-form-${mode}`}>
      <select value={wh} onChange={(e) => setWh(e.target.value)} className={SEL} data-testid="ht-warehouse">
        <option value="">{mode === "transfer" ? "Kaynak depo" : "Depo seçin"}</option>
        {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
      </select>
      {mode === "transfer" && (
        <select value={tgt} onChange={(e) => setTgt(e.target.value)} className={SEL} data-testid="ht-target-warehouse">
          <option value="">Hedef depo</option>
          {warehouses.filter((w) => w.id !== wh).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      )}
      {wh && <div className="text-sm text-slate-400" data-testid="ht-current-stock">Mevcut: {fmt(row?.quantity)} · Kullanılabilir: {fmt(row?.available)}</div>}
      <input value={qty} onChange={(e) => setQty(e.target.value)} inputMode="decimal" placeholder={mode === "count" ? "Sayılan miktar" : "Miktar"}
        className={`${SEL} text-2xl font-mono text-center`} data-testid="ht-qty" />
      <Button disabled={!ok || busy} onClick={submit} className="w-full h-14 rounded-md bg-blue-600 hover:bg-blue-500 text-lg" data-testid="ht-submit">
        {busy && <Loader2 className="w-5 h-5 mr-2 animate-spin" />} Kaydet
      </Button>
    </div>
  );
}

export default function HandTerminal() {
  const { can } = useAuth();
  const [scan, setScan] = useState(false);
  const [unknown, setUnknown] = useState("");
  const [manual, setManual] = useState(false);
  const [item, setItem] = useState(null);
  const [mode, setMode] = useState("");
  const [serial, setSerial] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [rows, setRows] = useState([]);
  const canEdit = can("warehouse", "edit");

  useEffect(() => { api.get("/warehouses").then(({ data }) => setWarehouses((data || []).filter((w) => w.is_active !== false))).catch(() => {}); }, []);
  const loadStock = useCallback(async (it) => {
    if (!it) return;
    const { data } = await api.get("/inventory/stock", { params: { product_id: it.product.id, variant_id: it.variant?.id || "" } });
    setRows(Array.isArray(data) ? data : []);
  }, []);
  useEffect(() => { loadStock(item).catch(() => setRows([])); }, [item, loadStock]);

  const onCode = async (code) => {
    try {
      const r = await lookupBarcode(code);
      if (r.found) { setItem(r); setMode(""); } else setUnknown(r.code);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const onManual = async (line) => {
    let variant = null;
    if (line.variant_id) {
      const { data } = await api.get(`/products/${line.material_id}/variants`);
      variant = (data || []).find((v) => v.id === line.variant_id) || null;
    }
    setItem({ product: { id: line.material_id, name: line.product_name }, variant, size: variant ? formatVariantLabel(variant.attributes, variant.sku) : "" });
    setManual(false); setMode("");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 max-w-lg mx-auto space-y-4" data-testid="hand-terminal">
      <h1 className="text-2xl font-semibold tracking-tight">El Terminali</h1>
      <div className="grid grid-cols-2 gap-3">
        <Button onClick={() => setScan(true)} className="h-20 rounded-md bg-blue-600 hover:bg-blue-500 text-lg flex-col gap-1" data-testid="ht-scan-btn">
          <ScanLine className="w-7 h-7" /> Barkod Tara
        </Button>
        <Button onClick={() => setManual((m) => !m)} variant="outline" className="h-20 rounded-md border-slate-700 bg-slate-900 text-lg flex-col gap-1" data-testid="ht-search-btn">
          <Search className="w-7 h-7" /> Ürün Ara
        </Button>
      </div>
      {manual && <ManualFind onPick={onManual} />}
      {canEdit && (
        <Button onClick={() => setSerial((s) => !s)} variant="outline" className="w-full h-12 rounded-md border-amber-700 bg-slate-900 text-amber-200 text-base" data-testid="ht-serial-btn">
          <ClipboardCheck className="w-5 h-5 mr-2" /> {serial ? "Seri Sayımı Kapat" : "Seri Sayım (okut → +1)"}
        </Button>
      )}
      {serial && canEdit && <SerialCount warehouses={warehouses} />}

      {item && (
        <div className="border border-slate-800 rounded-md p-4 space-y-3 bg-slate-900/50" data-testid="ht-item">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-lg font-semibold" data-testid="ht-item-name">{item.product.name}</div>
              <div className="text-sm text-slate-400 font-mono" data-testid="ht-item-variant">
                {item.size ? `Beden: ${item.size}` : item.variant ? item.variant.sku : "Varyantsız"}{item.variant?.barcode ? ` · ${item.variant.barcode}` : ""}
              </div>
            </div>
            <button onClick={() => { setItem(null); setMode(""); }} className="text-slate-500" data-testid="ht-item-clear"><X className="w-5 h-5" /></button>
          </div>
          <div className="text-xs font-mono text-slate-400 space-y-0.5" data-testid="ht-stock-rows">
            {rows.length ? rows.map((r) => <div key={r.warehouse_id}>{r.warehouse_name}: {fmt(r.quantity)} (kullanılabilir {fmt(r.available)})</div>) : "Stok kaydı yok"}
          </div>
          {canEdit ? (
            <div className="grid grid-cols-2 gap-2">
              {MODES.map(([k, l, Icon, c]) => (
                <Button key={k} onClick={() => setMode(k)} className={`h-14 rounded-md ${mode === k ? c : "bg-slate-800"} hover:opacity-90 text-base`} data-testid={`ht-mode-${k}`}>
                  <Icon className="w-5 h-5 mr-2" /> {l}
                </Button>
              ))}
            </div>
          ) : <div className="text-sm text-slate-400" data-testid="ht-no-perm">Stok işlemi yetkiniz yok (warehouse.edit).</div>}
          {mode && canEdit && <StockAction key={`${mode}-${item.variant?.id || item.product.id}`} item={item} mode={mode} warehouses={warehouses} rows={rows} onDone={() => loadStock(item)} />}
        </div>
      )}

      <BarcodeScanner open={scan} onOpenChange={setScan} onDetected={onCode} />
      <UnknownBarcodeDialog code={unknown} onClose={() => setUnknown("")} onAssigned={(r) => { setUnknown(""); setItem(r); setMode(""); }} />
    </div>
  );
}
